import { BorrowMarketContent } from '@/components/borrowMarket/borrowMarketContent'
import { BorrowMarketHeader } from '@/components/borrowMarket/borrowMarketHeader'
import { BorrowMarketSidePanel } from '@/components/borrowMarket/borrowMarketSidePanel'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Content, Page } from '@/components/layout/wrappers'
import { queryClient } from '@/config/query/queryClient'
import { ALL_SDK_NETWORKS } from '@/config/query/queryParams'
import { borrowMarketQueryOptions, useBorrowMarketQuery } from '@/queries/borrowMarket.query'
import { cn } from '@/utils/cn'
import { getMetaTags } from '@/utils/meta/getMetaTags'
import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'
import { isAddress, isAddressEqual } from 'viem'
import { z } from 'zod'

const borrowMarketPageParamsSchema = z.object({
  network: z.enum(ALL_SDK_NETWORKS),
  marketId: z.string().min(1),
  assetId: z.string().min(1),
})

export async function loader({ params }: LoaderFunctionArgs) {
  const parseResult = borrowMarketPageParamsSchema.safeParse(params)
  if (!parseResult.success) {
    throw new Response(null, { status: 404, statusText: 'Not Found' })
  }

  const { network, marketId, assetId } = parseResult.data
  const market = await queryClient.ensureQueryData(borrowMarketQueryOptions({ network, marketId })).catch(() => null)

  if (!market) {
    throw new Response(null, { status: 404, statusText: 'Not Found' })
  }

  const selectedAsset = market.assetsData.find(
    (assetData) =>
      assetData.marketAssetId === assetId || (isAddress(assetId) && isAddressEqual(assetData.asset.address, assetId))
  )
  if (!selectedAsset?.assetConfig.isBorrowEnabled) {
    throw new Response(null, { status: 404, statusText: 'Not Found' })
  }

  return { assetId, initialBorrowMarket: market, selectedAsset }
}

export type BorrowMarketLoader = typeof loader

export const meta: MetaFunction<BorrowMarketLoader> = ({ data, location }) =>
  getMetaTags({
    title: data ? `${data.selectedAsset.asset.symbol} borrow | ${data.initialBorrowMarket.name}` : 'Borrow market',
    description: data
      ? `View ${data.selectedAsset.asset.symbol} borrow market details for ${data.initialBorrowMarket.name}.`
      : 'View borrow market details.',
    url: location.pathname,
  })

export default function BorrowMarket() {
  const { assetId, initialBorrowMarket, selectedAsset: initialSelectedAsset } = useLoaderData<BorrowMarketLoader>()
  const { data: market = initialBorrowMarket } = useBorrowMarketQuery({
    network: initialBorrowMarket.network.name,
    marketId: initialBorrowMarket.marketId,
    initialData: initialBorrowMarket,
  })
  const selectedAsset =
    market.assetsData.find(
      (assetData) =>
        assetData.marketAssetId === assetId || (isAddress(assetId) && isAddressEqual(assetData.asset.address, assetId))
    ) ?? initialSelectedAsset

  return (
    <Page>
      <ScrollArea className="h-full">
        <Content>
          <BorrowMarketHeader
            marketName={market.name}
            assetSymbol={selectedAsset.asset.symbol}
            protocol={market.protocol}
          />
          <div className="mt-4 flex flex-col lg:grid lg:grid-cols-[1fr_24rem] lg:gap-8">
            <div className="flex flex-col gap-4 overflow-hidden lg:grid lg:h-fit lg:max-w-full">
              <BorrowMarketContent market={market} borrowAssetData={selectedAsset} />
            </div>
            <BorrowMarketSidePanel className={cn('sticky top-6 max-w-96 overflow-hidden', 'hidden lg:flex')} />
          </div>
        </Content>
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </Page>
  )
}
